import { type Language, persistUserLanguage, readUserPersistedLanguage } from '@/lib/language-prefs';

const MARKETING_PREFIX_LANGS = ['en', 'es', 'de', 'it', 'pt', 'lb', 'uk', 'ru', 'ar', 'tr', 'zgh'] as const;

/** Routes marketing servies par Astro (FR) ou SPA React (autres langues). */
export const MARKETING_STACK_PATHS = new Set([
  '/',
  '/pricing',
  '/features',
  '/about',
  '/contact',
  '/demo',
  '/aide',
  '/accessibility',
  '/use-cases',
  '/partners',
  '/privacy',
  '/terms',
  '/cookies',
]);

const LANG_PREFIX_RE = new RegExp(`^/(${MARKETING_PREFIX_LANGS.join('|')})(/|$)`);

/** Retire le préfixe `/en`, `/es`, etc. pour cibler la route SPA canonique. */
export function stripMarketingLangPrefix(pathname: string): string {
  const path = pathname.split('?')[0]?.split('#')[0] ?? '/';
  const m = path.match(new RegExp(`^/(${MARKETING_PREFIX_LANGS.join('|')})(/.*)?$`));
  if (!m) return path || '/';
  return m[2] || '/';
}

export function isMarketingStackRoute(pathname: string): boolean {
  return MARKETING_STACK_PATHS.has(stripMarketingLangPrefix(pathname));
}

/** Persiste la langue (cookie + localStorage) et recharge la page sans préfixe locale. */
export function applyMarketingLanguageNavigation(language: Language): void {
  persistUserLanguage(language);
  if (typeof window === 'undefined') return;
  const path = stripMarketingLangPrefix(window.location.pathname);
  window.location.assign(`${path}${window.location.search}${window.location.hash}`);
}

function readMarketingLanguageCookie(): Language | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)app_language=([^;]+)/);
  const raw = match?.[1] ? decodeURIComponent(match[1]) : '';
  return raw === 'fr' || MARKETING_PREFIX_LANGS.includes(raw as (typeof MARKETING_PREFIX_LANGS)[number])
    ? (raw as Language)
    : null;
}

/** Au chargement Astro : bascule vers le SPA si une langue non-FR est déjà persistée. */
export function syncMarketingStackRouting(): void {
  if (typeof window === 'undefined') return;
  const lang = readUserPersistedLanguage();
  if (!lang || lang === 'fr' || !isMarketingStackRoute(window.location.pathname)) return;

  const cookieLang = readMarketingLanguageCookie();
  if (cookieLang === lang) return;

  applyMarketingLanguageNavigation(lang);
}

export function isMarketingLangPrefixPath(pathname: string): boolean {
  return LANG_PREFIX_RE.test(pathname.split('?')[0]?.split('#')[0] ?? '');
}
